/**
 * Single source of truth for which roles may access each `/tools/:page`.
 *
 * Mirrors the per-link `roles` definitions in the SideMenu, which is what
 * decides whether a navigation link is shown. The DashboardPage renders every
 * `/tools/:page` route, so it must enforce the same rules — otherwise a user
 * can reach a page the SideMenu hides simply by typing the URL.
 *
 * Pages NOT listed here (e.g. settings) carry no role restriction and are
 * available to any authenticated user.
 *
 * NOTE: `admin` includes `recruiter` to preserve the existing DashboardPage
 * behaviour (recruiters could already reach the admin console).
 */
export const PAGE_ROLES = {
  discover: ['admin', 'recruiter'],
  dashboard: ['admin', 'recruiter'],
  'interview-dashboard': ['interviewer'],
  'company-dashboard': ['client'],
  companies: ['admin', 'recruiter'],
  jobs: ['admin', 'recruiter', 'client'],
  candidates: ['admin', 'recruiter', 'client'],
  calendar: ['admin', 'recruiter', 'interviewer', 'client'],
  reports: ['admin', 'recruiter'],
  'intel-search': ['admin', 'recruiter'],
  search: ['admin', 'recruiter'],
  chat: ['admin', 'recruiter'],
  map: ['admin', 'recruiter'],
  admin: ['admin', 'recruiter'],
};

/**
 * Whether the given roles may access the given page.
 * Unguarded pages (not in PAGE_ROLES) are always allowed.
 */
export const isPageAllowed = (page, userRoles = []) => {
  const allowedRoles = PAGE_ROLES[page];
  if (!allowedRoles) {
    return true;
  }
  return allowedRoles.some((role) => userRoles.includes(role));
};

/**
 * The landing route for a user, used to redirect away from a page they are
 * not permitted to view. Interviewers and clients have dedicated dashboards;
 * everyone else (admin/recruiter) goes to their first available link, with a
 * non-guarded fallback to avoid redirect loops when nothing is available.
 */
export const getRoleHomePath = (userRoles = [], firstAvailableLink) => {
  const has = (role) => userRoles.includes(role);

  if (!has('admin') && !has('recruiter')) {
    if (has('interviewer')) {
      return '/tools/interview-dashboard';
    }
    if (has('client')) {
      return '/tools/company-dashboard';
    }
  }

  return firstAvailableLink || '/tools/settings/profile';
};
